/**
 * ⌘K / Ctrl+K — search everything you can read, from anywhere in the module.
 *
 * The server does ALL the work: ranking, typo tolerance, content matching,
 * and the Uzbek Latin⇄Cyrillic bridge. This component only debounces input,
 * renders the two result groups, and navigates — which is why `shouldFilter`
 * is off: letting cmdk re-filter server-ranked results would re-break the
 * cross-script matching the backend went to lengths to provide.
 *
 * Snippets arrive as plain text with [[ ]] around matches. They are split on
 * those sentinels and wrapped in <mark> here — never injected as HTML.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { IndexStatusBadge } from '@/modules/my-docs/components/listing/IndexStatusBadge.jsx';
import { FileText, Folder, Search } from 'lucide-react';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Loader2 } from 'lucide-react';

import { KbdChip } from '@/shared/components/KbdChip.jsx';
import { useGetRequest } from '@/shared/hooks/requests.js';
import { COMMAND_PALETTE_EVENT, markSearchHintSeen } from '@/shared/utils/commandPalette.js';
import { shortcutLabel } from '@/shared/utils/platform.js';
import { currentUser, iHold } from '@/shared/auth/me.js';
import { SCOPE } from '@/shared/auth/scopes.js';
import { SEARCH } from '@/shared/utils/urls.js';

const DEBOUNCE_MS = 300;
const MIN_QUERY = 2;

/** Routes where a global overlay would be wrong: share views and the
 * verification page reached without signing in, the agent panel living
 * inside Collabora, and the provider pages another module frames or opens
 * (`/embed/sign`, `/embed/intent/…`) — their host owns navigation, and a
 * search palette that leads to /my-docs has nowhere to go there. */
const EXCLUDED_PREFIXES = ['/preview', '/share/', '/guest-share/', '/v/', '/agent-panel', '/service', '/embed/'];

/** Split "…[[match]]…" into text and <mark> nodes — no HTML injection. */
function renderSnippet(snippet) {
  if (!snippet) return null;
  const parts = snippet.split(/\[\[|\]\]/);
  // Odd indices are the matched fragments (text [[m]] text [[m]] …).
  return parts.map((part, index) =>
    index % 2 === 1 ? <mark key={index}>{part}</mark> : <span key={index}>{part}</span>,
  );
}

export function CommandPalette() {
  const { t } = useTranslation(['search', 'my-docs']);
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const timer = useRef(null);
  const searchRequest = useGetRequest({ showError: false });

  const excluded = EXCLUDED_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));
  // Only for a signed-in person who may find documents (`files:read`, what
  // docs-api's search asks; shared/auth/me.js, read on every render, and
  // opening the palette is one).
  const signedIn = Boolean(currentUser()) && iHold(SCOPE.FILES_READ);
  // Empty-query state → offer quick actions instead of an idle hint.
  const isEmpty = query.trim().length < MIN_QUERY;
  const go = useCallback((path) => { setOpen(false); navigate(path); }, [navigate]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((previous) => !previous);
      }
    };
    // The discoverability affordances (toolbar chip, top-bar button, empty
    // state) open the palette through this event — they live in trees this
    // component is deliberately outside of.
    const onPaletteEvent = (event) => {
      const action = event.detail?.action ?? 'open';
      if (action === 'close') setOpen(false);
      else if (action === 'toggle') setOpen((previous) => !previous);
      else setOpen(true);
    };
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener(COMMAND_PALETTE_EVENT, onPaletteEvent);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener(COMMAND_PALETTE_EVENT, onPaletteEvent);
    };
  }, []);

  const runSearch = useCallback(
    async (term) => {
      const { response, success } = await searchRequest.request({
        url: SEARCH,
        params: { q: term, size: 20 },
      });
      if (success) setResults(response);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Debounced: one request per pause in typing, none under the minimum.
  useEffect(() => {
    if (!open) return undefined;
    clearTimeout(timer.current);
    const term = query.trim();
    if (term.length < MIN_QUERY) {
      setResults(null);
      return undefined;
    }
    timer.current = setTimeout(() => runSearch(term), DEBOUNCE_MS);
    return () => clearTimeout(timer.current);
  }, [query, open, runSearch]);

  // Reset per open so yesterday's results never flash before today's — and
  // retire the first-run coach mark: whoever opened this knows it exists.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults(null);
    } else {
      markSearchHintSeen();
    }
  }, [open]);

  const { folders, files } = useMemo(() => {
    const items = results?.items ?? [];
    return {
      folders: items.filter((item) => item.nodeType === 'folder'),
      files: items.filter((item) => item.nodeType === 'file'),
    };
  }, [results]);

  // Hits name nodes by uuid (`uuid`, `parentUuid`). A folder opens; a file
  // opens in its folder (`?file=`; the root belongs to /my-docs).
  const openHit = useCallback(
    (item) => {
      setOpen(false);
      if (item.nodeType === 'folder') {
        navigate(`/files/${item.uuid}`);
      } else {
        const file = `?file=${encodeURIComponent(item.uuid)}`;
        navigate(item.parentUuid ? `/files/${item.parentUuid}${file}` : `/my-docs${file}`);
      }
    },
    [navigate],
  );

  if (!signedIn || excluded) return null;

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      shouldFilter={false}
      title={t('search:title')}
      description={t('search:hint')}
      showCloseButton={false}
      overlayClassName="bg-white/35 backdrop-blur-[24px] backdrop-saturate-150 dark:bg-[rgba(10,12,16,0.45)] zaip-fade-in"
      className="top-[22%] translate-y-0 rounded-[18px] border border-white/40 bg-white/60 shadow-[0_8px_32px_rgba(0,0,0,0.10)] backdrop-blur-[16px] sm:max-w-xl dark:border-white/10 dark:bg-zinc-900/60 zaip-panel-in"
      commandClassName="bg-transparent rounded-[18px]"
    >
      {/* The aurora rim — aip's orb palette drifting once per ~74s, masked
          to the border alone. */}
      <div aria-hidden className="zaip-aurora-ring pointer-events-none absolute -inset-px rounded-[18px]" />
      <div className="relative">
      <CommandInput
        placeholder={t('search:placeholder')}
        value={query}
        onValueChange={setQuery}
        wrapperClassName="h-12 border-white/40 px-4 dark:border-zinc-700/50"
        trailing={
          searchRequest.loading ? (
            <Loader2 size={14} className="animate-spin shrink-0 text-muted-foreground" />
          ) : null
        }
      />
      <CommandList>
        {isEmpty && (
          <CommandGroup
            heading={t('search:quickActions')}
            className="[&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-zinc-400"
          >
            <CommandItem
              value="qa-my-docs"
              onSelect={() => go('/my-docs')}
              className="rounded-[14px] transition data-[selected=true]:bg-white/60 data-[selected=true]:shadow-sm dark:data-[selected=true]:bg-zinc-800/60"
            >
              <Folder className="mr-2 h-4 w-4 shrink-0 text-brand" />
              <span>{t('search:actions.myDocs')}</span>
            </CommandItem>
          </CommandGroup>
        )}
        {results && folders.length === 0 && files.length === 0 && (
          <CommandEmpty>{t('search:empty')}</CommandEmpty>
        )}

        {folders.length > 0 && (
          <CommandGroup
            heading={t('search:groupFolders')}
            className="[&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-zinc-400"
          >
            {folders.map((item) => (
              <CommandItem
                key={`folder-${item.uuid}`}
                value={`folder-${item.uuid}`}
                onSelect={() => openHit(item)}
                className="rounded-[14px] transition data-[selected=true]:bg-white/60 data-[selected=true]:shadow-sm dark:data-[selected=true]:bg-zinc-800/60"
              >
                <Folder className="mr-2 h-4 w-4 shrink-0 text-primary" />
                <span className="truncate">{item.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {files.length > 0 && (
          <CommandGroup
            heading={t('search:groupFiles')}
            className="[&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-zinc-400"
          >
            {files.map((item) => (
              <CommandItem
                key={`file-${item.uuid}`}
                value={`file-${item.uuid}`}
                onSelect={() => openHit(item)}
                className="rounded-[14px] transition data-[selected=true]:bg-white/60 data-[selected=true]:shadow-sm dark:data-[selected=true]:bg-zinc-800/60"
              >
                <FileText className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate">{item.name}</span>
                    {item.matchType !== 'name' && (
                      <span className="shrink-0 rounded-full bg-teal-500/15 px-2 py-0.5 text-[11px] font-medium text-teal-700 dark:text-teal-300">
                        {t(`search:match.${item.matchType}`)}
                      </span>
                    )}
                    <IndexStatusBadge
                      status={item.indexStatus}
                      className="shrink-0"
                    />
                  </div>
                  {item.snippet && (
                    <div className="truncate text-xs text-muted-foreground [&_mark]:rounded-sm [&_mark]:bg-amber-200 [&_mark]:px-0.5 dark:[&_mark]:bg-amber-700/60">
                      {renderSnippet(item.snippet)}
                    </div>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {!results && !isEmpty && (
          <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
            <Search className="h-4 w-4" />
            {t('search:hint')}
          </div>
        )}
      </CommandList>

      {/* The learning loop closes here: every open shows the shortcut. */}
      <div className="flex items-center gap-3 border-t border-white/30 px-4 py-2 text-[11px] text-zinc-500 dark:border-white/10 dark:text-zinc-400">
        <span className="flex items-center gap-1">
          <KbdChip>↑↓</KbdChip> {t('search:footer.navigate')}
        </span>
        <span className="flex items-center gap-1">
          <KbdChip>↵</KbdChip> {t('search:footer.open')}
        </span>
        <span className="flex items-center gap-1">
          <KbdChip>esc</KbdChip> {t('search:footer.close')}
        </span>
        <KbdChip className="ml-auto">{shortcutLabel()}</KbdChip>
      </div>
      </div>
    </CommandDialog>
  );
}
